// Diagrams Agent API — two-phase trigger + ticket polling + repo CRUD.
// Uses the same shared AppSync endpoint as Research Deck / Reporter / Ad Agency,
// routed by jobType ("TRIGGER_DIAGRAMS_INTERPRET_JOB" / "TRIGGER_DIAGRAMS_RENDER_JOB").

import { ApolloClient, InMemoryCache, createHttpLink, gql } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  TRIGGER_AD_AGENCY_JOB_MUTATION,
  GET_LIBRARY_CARD_QUERY,
} from "../queries/libraryCardQueries";
import { getAuthToken } from "../utils/authUtils";

const GRAPHQL_ENDPOINT =
  "https://m2jxbyhagffjthxtsiuwb2iiwe.appsync-api.us-east-1.amazonaws.com/graphql";

const httpLink = createHttpLink({ uri: GRAPHQL_ENDPOINT });

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: token } : {}),
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

/** Generate a runId — format mirrors research-deck: `diag_<ms>_<4-byte-hex>`. */
export const generateRunId = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(4));
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `diag_${Date.now()}_${hex}`;
};

// ─── Phase A: interpret ───────────────────────────────────────────────────
// payload: { runId, tenantId, topic, repos?, direction, lineStyle }
export const triggerDiagramsInterpretApi = async (payload) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_DIAGRAMS_INTERPRET_JOB",
        tenantId: payload.tenantId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// ─── Phase B: render ──────────────────────────────────────────────────────
// payload: { runId, tenantId, confirmedScope, repoMap? }
export const triggerDiagramsRenderApi = async (payload) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_DIAGRAMS_RENDER_JOB",
        tenantId: payload.tenantId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// ─── Flowchart: single-shot pipeline (no interpret/confirm step) ──────────
// payload: { runId, tenantId, topic, direction, lineStyle }
// Backed by TRIGGER_FLOWCHART_JOB on the BE — DiagramsTrigger starts the
// flowchart SFN directly; the FE just polls the same DIAGRAMSTICKET row.
export const triggerFlowchartApi = async (payload) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_FLOWCHART_JOB",
        tenantId: payload.tenantId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// ─── Chart: single-shot matplotlib pipeline ───────────────────────────────
// payload: { runId, tenantId, chartType, chartTitle?, chartXLabel?,
//            chartYLabel?, chartData? }
// Backed by TRIGGER_CHART_JOB on the BE.
export const triggerChartApi = async (payload) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_CHART_JOB",
        tenantId: payload.tenantId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// ─── Ticket polling — DIAGRAMSTICKET row ──────────────────────────────────
// Returns the parsed `data` field (status, phase, progress, proposedScope,
// diagrams, ...) or null if the row hasn't been written yet.
export const getDiagramsTicketApi = async ({ tenantId, runId }) => {
  try {
    const { data } = await client.query({
      query: GET_LIBRARY_CARD_QUERY,
      variables: {
        input: {
          operationType: "GET_LIBRARY_CARD",
          tenantId,
          entityType: "DIAGRAMSTICKET",
          entityId: runId,
        },
      },
      fetchPolicy: "no-cache",
    });
    const card = data?.getLibraryCard;
    if (!card || !card.data) return null;
    return typeof card.data === "string" ? JSON.parse(card.data) : card.data;
  } catch (err) {
    const msg = String(err?.message || "");
    if (msg.includes("not found")) return null;
    throw err;
  }
};

// ─── Repo CRUD — DIAGRAMSREPO library cards ───────────────────────────────
// Used by both the main page (dropdown population) and the Manage Repos
// subpage (full CRUD). Mirrors the library-card conventions used elsewhere.

const LIST_LIBRARY_CARDS_QUERY = gql`
  query ListLibraryCards($input: ListLibraryCardsInput!) {
    listLibraryCards(input: $input) {
      pageNames
    }
  }
`;

const SAVE_LIBRARY_CARD_MUTATION = gql`
  mutation SaveLibraryCard($input: SaveLibraryCardInput!) {
    saveLibraryCard(input: $input) {
      success
      message
      record
    }
  }
`;

/** Returns the array of parsed DIAGRAMSREPO cards for this tenant. */
export const listDiagramsReposApi = async ({ tenantId }) => {
  // listLibraryCards returns pageNames (entity IDs); we then fetch each one.
  // For small dropdowns this is fine; if we grow past ~50 repos we'll add a
  // bulk fetch API instead.
  const { data } = await client.query({
    query: LIST_LIBRARY_CARDS_QUERY,
    variables: {
      input: {
        operationType: "LIST_LIBRARY_CARDS",
        tenantId,
        entityType: "DIAGRAMSREPO",
      },
    },
    fetchPolicy: "no-cache",
  });
  const ids = data?.listLibraryCards?.pageNames || [];
  const rows = await Promise.all(
    ids.map((id) =>
      client
        .query({
          query: GET_LIBRARY_CARD_QUERY,
          variables: {
            input: {
              operationType: "GET_LIBRARY_CARD",
              tenantId,
              entityType: "DIAGRAMSREPO",
              entityId: id,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((r) => {
          const card = r.data?.getLibraryCard;
          if (!card || !card.data) return null;
          const parsed = typeof card.data === "string" ? JSON.parse(card.data) : card.data;
          return { ...parsed, entityId: card.entityId || id };
        })
        .catch(() => null),
    ),
  );
  return rows.filter(Boolean);
};

/**
 * Create or update a DIAGRAMSREPO card.
 *  { repoSlug, displayName?, branch?, description?, order?, active? }
 */
export const saveDiagramsRepoApi = async ({ tenantId, repo }) => {
  const entityId = repo.repoSlug.replace(/[^a-zA-Z0-9._-]/g, "_");
  const now = new Date().toISOString();
  const payload = {
    repoSlug: repo.repoSlug,
    displayName: repo.displayName || repo.repoSlug.split("/").pop(),
    branch: repo.branch || "develop",
    description: repo.description || "",
    order: typeof repo.order === "number" ? repo.order : 999,
    active: repo.active !== false,
    updatedAt: now,
    createdAt: repo.createdAt || now,
  };
  const { data } = await client.mutate({
    mutation: SAVE_LIBRARY_CARD_MUTATION,
    variables: {
      input: {
        operationType: "SAVE_LIBRARY_CARD",
        tenantId,
        entityType: "DIAGRAMSREPO",
        entityId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.saveLibraryCard;
};

/** Soft-delete by setting active=false. (Hard delete requires BE support we'd add later.) */
export const deleteDiagramsRepoApi = async ({ tenantId, repoSlug }) =>
  saveDiagramsRepoApi({ tenantId, repo: { repoSlug, active: false } });
